import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { env } from '../../config/env';
import { ApiLocalStorage } from '../../utils/ApiLocalStorage';
import { SCHEMA_STORAGE_KEY } from 'modules/account/services/SchemaService';

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    const normalizedBaseURL = baseURL.replace(/\/+$/, '');
    
    this.axiosInstance = axios.create({
      baseURL: normalizedBaseURL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.initializeResponseInterceptor();
  }

  private initializeResponseInterceptor = () => {
    this.axiosInstance.interceptors.response.use(
      this.handleResponse,
      this.handleError
    );
  };

  private handleResponse = ({ data }: AxiosResponse) => data;

  private handleError = (error: any) => Promise.reject(error);

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const normalizedUrl = url.replace(/^\/+/, '');
    const schema = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);

    const response: AxiosResponse<T> = await this.axiosInstance.get<T>(`${normalizedUrl}`, {
      ...config,
      headers: {
        ...config?.headers,
        'Cache-Control': 'no-cache',
        'Tenant': schema?.tenantId
      }
    });

    return response as any;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const schema = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
    
    const response: AxiosResponse<T> = await this.axiosInstance.delete<T>(url, {
      ...config,
      headers: {
        ...config?.headers,
        'Cache-Control': 'no-cache',
        'Tenant': schema?.tenantId
      }
    });

    return response.data;
  }

  public async put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
    const schema = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
    const accessToken = ApiLocalStorage.get('@pwa:access-token');
    
    const response: AxiosResponse<T> = await this.axiosInstance.put<T>(url, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Authorization': `Bearer ${accessToken}`,
        'Cache-Control': 'no-cache',
        'Tenant': schema?.tenantId
      }
    });

    return response.data;
  }

  public async post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> {
    const schema = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
    
    const response = await this.axiosInstance.post<T>(url, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Cache-Control': 'no-cache',
        'Tenant': schema?.tenantId
      }
    });

    return (response.data && Object.keys(response.data).length > 0) 
      ? response.data 
      : response as any;
  }
}

const apiUserAccount = new ApiService(env.API_URL || '');
const apiAssets = new ApiService('../../');

const apiAssetsHost = new ApiService(
  `${	process.env.REACT_APP_ASSETS_ENDPOINT}`
);

const apiSchemaHost = new ApiService(
  `${	process.env.REACT_APP_SCHEMA_ENDPOINT}`
);

export { apiUserAccount, apiAssetsHost, apiSchemaHost, apiAssets };

